import type {
	BrazeArticleContext,
	BrazeMessagesInterface,
} from '@guardian/braze-components/logic';
import { cmp } from '@guardian/consent-manager';
import type { CountryCode } from '@guardian/libs';
import { isString, isUndefined, storage } from '@guardian/libs';
import type { ModuleData } from '@guardian/support-dotcom-components/dist/dotcom/types';
import type { BannerProps } from '@guardian/support-dotcom-components/dist/shared/types';
import { useEffect, useState } from 'react';
import type { ArticleCounts } from '../lib/articleCount';
import { getArticleCounts } from '../lib/articleCount';
import {
	BrazeBannersSystemPlacementId,
	buildBrazeBannersSystemConfig,
} from '../lib/braze/BrazeBannersSystem';
import type {
	CandidateConfig,
	PickMessageResult,
	SlotConfig,
} from '../lib/messagePicker';
import { pickMessage } from '../lib/messagePicker';
import { useAB } from '../lib/useAB';
import { useIsSignedIn } from '../lib/useAuthStatus';
import { useBraze } from '../lib/useBraze';
import { useCountryCode } from '../lib/useCountryCode';
import { usePageViewId } from '../lib/usePageViewId';
import type { RenderingTarget } from '../types/renderingTarget';
import type { TagType } from '../types/tag';
import { useConfig } from './ConfigContext';
import type { AuxiaGateDisplayData } from './SignInGate/types';
import {
	BrazeBanner,
	canShowBrazeBanner,
} from './StickyBottomBanner/BrazeBanner';
import {
	canShowRRBanner,
	ReaderRevenueBanner,
} from './StickyBottomBanner/ReaderRevenueBanner';
import type { CanShowFunctionType } from './StickyBottomBanner/ReaderRevenueBanner';
import type { CanShowSignInGateProps } from './StickyBottomBanner/SignInGatePortal';
import {
	canShowSignInGatePortal,
	SignInGatePortal,
} from './StickyBottomBanner/SignInGatePortal';

type Props = {
	contentType: string;
	sectionId: string;
	tags: TagType[];
	isPaidContent: boolean;
	isPreview: boolean;
	shouldHideReaderRevenue: boolean;
	isMinuteArticle: boolean;

	contributionsServiceUrl: string;
	idApiUrl: string;

	pageId: string;
	host?: string;
};

type BrazeMeta = {
	dataFromBraze: Record<string, string>;
	logImpressionWithBraze: () => void;
	logButtonClickWithBraze: (id: number) => void;
};

type RRBannerConfig = {
	id: string;
	BannerComponent: typeof ReaderRevenueBanner;
	canShowFn: CanShowFunctionType<ModuleData<BannerProps>>;
	isEnabled: boolean;
};

const getBannerLastClosedAt = (key: string): string | undefined => {
	const item = storage.local.get(`gu.prefs.${key}`);
	return isString(item) ? item : undefined;
};

const DEFAULT_BANNER_TIMEOUT_MILLIS = 2000;

const buildCmpBannerConfig = (): CandidateConfig<void> => ({
	candidate: {
		id: 'cmpUi',
		canShow: () =>
			cmp
				.willShowPrivacyMessage()
				.then((result) =>
					result ? { show: true, meta: undefined } : { show: false },
				), // Intentionally there is no catch block because if the CMP fails no other banner can be displayed.
		show: () => {
			// New CMP is not a react component and is shown outside of react's world
			// so render nothing if it will show
			return null;
		},
	},
	timeoutMillis: null,
});

const buildRRBannerConfigWith = ({
	id,
	BannerComponent,
	canShowFn,
	isEnabled,
}: RRBannerConfig) => {
	return ({
		isSignedIn,
		countryCode,
		isPreview,
		asyncArticleCounts,
		contentType,
		sectionId,
		shouldHideReaderRevenue,
		isMinuteArticle,
		isPaidContent,
		isSensitive,
		tags,
		contributionsServiceUrl,
		idApiUrl,
		renderingTarget,
		ophanPageViewId,
		pageId,
		inHoldbackGroup,
		inAuxiaVariant,
	}: {
		isSignedIn: boolean;
		countryCode: CountryCode;
		isPreview: boolean;
		asyncArticleCounts: Promise<ArticleCounts | undefined>;
		contentType: string;
		sectionId: string;
		shouldHideReaderRevenue: boolean;
		isMinuteArticle: boolean;
		isPaidContent: boolean;
		isSensitive: boolean;
		tags: TagType[];
		contributionsServiceUrl: string;
		idApiUrl: string;
		renderingTarget: RenderingTarget;
		ophanPageViewId: string;
		pageId?: string;
		inHoldbackGroup?: boolean;
		inAuxiaVariant?: boolean;
	}): CandidateConfig<ModuleData<BannerProps>> => {
		return {
			candidate: {
				id,
				canShow: () =>
					canShowFn({
						remoteBannerConfig: isEnabled,
						isSignedIn,
						countryCode,
						contentType,
						sectionId,
						shouldHideReaderRevenue,
						isMinuteArticle,
						isPaidContent,
						isSensitive,
						tags,
						contributionsServiceUrl,
						engagementBannerLastClosedAt: getBannerLastClosedAt(
							'engagementBannerLastClosedAt',
						),
						subscriptionBannerLastClosedAt: getBannerLastClosedAt(
							'subscriptionBannerLastClosedAt',
						),
						signInBannerLastClosedAt: getBannerLastClosedAt(
							'signInBannerLastClosedAt',
						),
						abandonedBasketBannerLastClosedAt:
							getBannerLastClosedAt(
								'abandonedBasketLastClosedAt',
							),
						isPreview,
						idApiUrl,
						renderingTarget,
						asyncArticleCounts,
						ophanPageViewId,
						pageId,
						inHoldbackGroup,
						inAuxiaVariant,
					}),
				show: ({ name, props }: ModuleData<BannerProps>) => (
					<BannerComponent name={name} props={props} />
				),
			},
			timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
		};
	};
};

const buildSignInGateConfig = (
	canShowProps: CanShowSignInGateProps,
	host?: string,
): CandidateConfig<AuxiaGateDisplayData> => ({
	candidate: {
		id: 'sign-in-gate-portal',
		canShow: async () => {
			return await canShowSignInGatePortal(canShowProps);
		},
		show: (meta: AuxiaGateDisplayData) => (
			<SignInGatePortal
				host={host}
				isPaidContent={canShowProps.isPaidContent}
				isPreview={canShowProps.isPreview}
				pageId={canShowProps.pageId}
				contributionsServiceUrl={canShowProps.contributionsServiceUrl}
				auxiaGateDisplayData={meta}
			/>
		),
	},
	timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
});

const buildReaderRevenueBannerConfig = (isEnabled: boolean) =>
	buildRRBannerConfigWith({
		id: 'reader-revenue-banner',
		BannerComponent: ReaderRevenueBanner,
		canShowFn: canShowRRBanner,
		isEnabled,
	});

const buildBrazeBanner = (
	brazeMessages: BrazeMessagesInterface,
	brazeArticleContext: BrazeArticleContext,
	idApiUrl: string,
	tags: TagType[],
	shouldHideReaderRevenue: boolean,
): CandidateConfig<BrazeMeta> => ({
	candidate: {
		id: 'braze-banner',
		canShow: () =>
			canShowBrazeBanner(
				brazeMessages,
				brazeArticleContext,
				tags,
				shouldHideReaderRevenue,
			),
		show: (meta: BrazeMeta) => (
			<BrazeBanner meta={meta} idApiUrl={idApiUrl} />
		),
	},
	timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
});

/**
 * The reader revenue banner at the end of articles
 *
 * ## Why does this need to be an Island?
 *
 * The content of the banner is personalised to an individual page view.
 *
 * ---
 *
 * (No visual story exists)
 */
export const StickyBottomBanner = ({
	contentType,
	sectionId,
	tags,
	isPaidContent,
	isPreview,
	shouldHideReaderRevenue,
	isMinuteArticle,
	isSensitive,
	contributionsServiceUrl,
	idApiUrl,
	pageId,
	remoteBannerSwitch,
	host,
}: Props & {
	remoteBannerSwitch: boolean;
	isSensitive: boolean;
}) => {
	const { renderingTarget, editionId } = useConfig();
	const { brazeMessages, braze } = useBraze(idApiUrl, renderingTarget);

	const countryCode = useCountryCode('sticky-bottom-banner');
	const isSignedIn = useIsSignedIn();
	const ophanPageViewId = usePageViewId(renderingTarget);
	const abTests = useAB();
	const inAuxiaVariant =
		abTests?.isUserInTestGroup('growth-auxia-banner', 'variant') ?? false;

	const [pickMessageResult, setPickMessageResult] =
		useState<PickMessageResult | null>(null);

	const [asyncArticleCounts, setAsyncArticleCounts] =
		useState<Promise<ArticleCounts | undefined>>();

	useEffect(() => {
		setAsyncArticleCounts(getArticleCounts(pageId, tags, contentType));
	}, [contentType, tags, pageId]);

	useEffect(() => {
		// Wait for the following dependencies before checking for CMP, Braze + RRCP messages
		if (
			isUndefined(countryCode) ||
			isUndefined(isSignedIn) ||
			isUndefined(brazeMessages) ||
			isUndefined(asyncArticleCounts) ||
			isUndefined(ophanPageViewId) ||
			isSignedIn === 'Pending'
		) {
			return;
		}
		const CMP = buildCmpBannerConfig();

		const readerRevenue = buildReaderRevenueBannerConfig(
			remoteBannerSwitch,
		)({
			isSignedIn,
			countryCode,
			isPreview,
			asyncArticleCounts,
			contentType,
			sectionId,
			shouldHideReaderRevenue,
			isMinuteArticle,
			isPaidContent,
			isSensitive,
			tags,
			contributionsServiceUrl,
			idApiUrl,
			renderingTarget,
			ophanPageViewId,
			pageId,
			inHoldbackGroup:
				abTests?.isUserInTestGroup(
					'growth-holdback-group',
					'control',
				) ?? false,
			inAuxiaVariant,
		});
		const brazeArticleContext: BrazeArticleContext = {
			section: sectionId,
		};
		const brazeBanner = buildBrazeBanner(
			brazeMessages,
			brazeArticleContext,
			idApiUrl,
			tags,
			shouldHideReaderRevenue,
		);
		const brazeBannersSystem = buildBrazeBannersSystemConfig(
			'braze-banners-system_StickyBottomBanner',
			BrazeBannersSystemPlacementId.Banner,
			braze,
			idApiUrl,
			contentType,
			shouldHideReaderRevenue,
			tags,
			window.guardian.config.stage,
		);

		const signInGate = buildSignInGateConfig(
			{
				isSignedIn,
				isPaidContent,
				isPreview,
				contentType,
				sectionId,
				tags,
				pageId,
				contributionsServiceUrl,
				editionId,
			},
			host,
		);

		const hasForceBannerParam =
			window.location.search.includes('force-banner');
		const hasPreviewBannerParam =
			window.location.search.includes('preview-banner');
		const hasForceBrazeMessageParam = window.location.hash.includes(
			'force-braze-message',
		);

		let candidates: SlotConfig['candidates'];

		if (hasForceBannerParam || hasPreviewBannerParam) {
			candidates = [CMP, readerRevenue];
		} else if (hasForceBrazeMessageParam) {
			candidates = [CMP, brazeBannersSystem, brazeBanner];
		} else {
			candidates = [
				CMP,
				signInGate,
				brazeBannersSystem,
				brazeBanner,
				readerRevenue,
			];
		}

		const bannerConfig: SlotConfig = {
			candidates,
			name: 'banner',
		};

		pickMessage(bannerConfig, renderingTarget)
			.then((result) => {
				setPickMessageResult(result);
			})
			.catch((e) => {
				// Report error to Sentry
				const msg = `StickyBottomBanner pickMessage - error: ${String(
					e,
				)}`;
				window.guardian.modules.sentry.reportError(
					new Error(msg),
					'sticky-bottom-banner',
				);
			});
	}, [
		isSignedIn,
		countryCode,
		brazeMessages,
		asyncArticleCounts,
		contentType,
		contributionsServiceUrl,
		editionId,
		idApiUrl,
		isMinuteArticle,
		isPaidContent,
		isPreview,
		isSensitive,
		remoteBannerSwitch,
		renderingTarget,
		sectionId,
		shouldHideReaderRevenue,
		tags,
		ophanPageViewId,
		pageId,
		host,
		braze,
		abTests,
		inAuxiaVariant,
	]);

	/**
	 * Custom events for commercial purposes to avoid the mobile-sticky ad slot clashing with these banners
	 * Dispatches events when no banner is due to appear and when the sign in gate is the chosen banner
	 * Please talk to @guardian/commercial-dev before changing this logic
	 */
	useEffect(() => {
		if (pickMessageResult?.type === 'NoMessageSelected') {
			document.dispatchEvent(new CustomEvent('banner:none'));
		}

		if (
			pickMessageResult?.type === 'MessageSelected' &&
			pickMessageResult.messageId === 'sign-in-gate-portal'
		) {
			document.dispatchEvent(new CustomEvent('banner:sign-in-gate'));
		}
	}, [pickMessageResult]);

	if (pickMessageResult?.type === 'MessageSelected') {
		const { SelectedMessage } = pickMessageResult;
		return <SelectedMessage />;
	}

	return null;
};
